import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  listAdsCategories,
  listAdsPostTagDefs,
  listAdsSocialPosts,
  listAdsTemplates,
  type AdsAssistantCategoryRow,
  type AdsAssistantPostTagDefRow,
  type AdsAssistantSocialPostRow,
  type AdsAssistantTemplateRow,
} from "@/lib/adsAssistantClient";
import { canEditAdsGuidance, canSeeAdsGraphExternalId, canSeeAdsSlotLabeledFields } from "@/lib/adsAssistantPermissions";
import { isManualIngestDirty } from "@/components/ads-assistant/isManualIngestDirty";
import { useAdsAssistantAggregationManualForm } from "@/components/ads-assistant/useAdsAssistantAggregationManualForm";
import { useAdsAssistantIndexFilters } from "@/components/ads-assistant/useAdsAssistantIndexFilters";
import { useAdsAssistantOrgLocations } from "@/components/ads-assistant/useAdsAssistantOrgLocations";

export function useAdsAssistantAggregation(scopedOrgId: string | null, variant: "full" | "browse") {
  const auth = useAuth();
  const canQuality = canEditAdsGuidance(auth);
  const canSeeSlotLabeledFields = canSeeAdsSlotLabeledFields(auth);
  const canSeeGraphExternalId = canSeeAdsGraphExternalId(auth);
  const canIngestPosts =
    auth.status === "ready" &&
    auth.hasRole(
      "sales",
      "marketing",
      "trainer",
      "admin",
      "owner",
      "dev",
      "platform_admin",
      "platform_dev",
      "platform_trainer",
      "platform_owner"
    );

  const [posts, setPosts] = useState<AdsAssistantSocialPostRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [categories, setCategories] = useState<AdsAssistantCategoryRow[]>([]);
  const [postTagDefs, setPostTagDefs] = useState<AdsAssistantPostTagDefRow[]>([]);
  const [templates, setTemplates] = useState<AdsAssistantTemplateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const indexFilters = useAdsAssistantIndexFilters();
  const { locations: orgLocations, locationsLoaded: orgLocationsLoaded } = useAdsAssistantOrgLocations(scopedOrgId);

  const ingestLocationId = useMemo(() => {
    if (!orgLocationsLoaded || !orgLocations.length) return null;
    const orgIds = new Set(orgLocations.map((l) => l.id));
    const preferred = auth.activeLocationId?.trim() || "";
    if (preferred && orgIds.has(preferred)) return preferred;
    const allowed = (auth.accessibleLocationIds ?? []).map((id) => id.trim()).filter(Boolean);
    const match = allowed.find((id) => orgIds.has(id));
    if (match) return match;
    if (orgLocations.length === 1) return orgLocations[0]!.id;
    return null;
  }, [orgLocations, orgLocationsLoaded, auth.activeLocationId, auth.accessibleLocationIds]);

  const load = useCallback(async () => {
    if (!scopedOrgId) return;
    setLoading(true);
    setError(null);
    const [r, c, t, pt] = await Promise.all([
      listAdsSocialPosts(indexFilters.indexListParams),
      listAdsCategories(),
      listAdsTemplates({ includeArchetypeTags: true }),
      listAdsPostTagDefs(),
    ]);
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setPosts([]);
      setTotal(null);
      return;
    }
    setPosts(r.data.posts ?? []);
    setTotal(typeof r.data.total === "number" ? r.data.total : (r.data.posts ?? []).length);
    if (c.ok) setCategories(c.data.categories ?? []);
    if (t.ok) setTemplates(t.data.templates?.filter((x) => x.is_active) ?? []);
    if (pt.ok) setPostTagDefs(pt.data.tags ?? []);
  }, [scopedOrgId, indexFilters.indexListParams]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!scopedOrgId) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load, scopedOrgId]);

  useEffect(() => {
    if (!saveMsg) return;
    const id = window.setTimeout(() => setSaveMsg(null), 2600);
    return () => window.clearTimeout(id);
  }, [saveMsg]);

  const manualForm = useAdsAssistantAggregationManualForm({
    scopedOrgId,
    canQuality,
    canSeePublishedAtOverride: canSeeSlotLabeledFields,
    templates,
    ingestLocationId,
    onSaved: () => void load(),
    setError,
    setSaveMsg,
  });

  const showManualForm = variant === "full";
  const isSalesOnly =
    auth.status === "ready" && auth.hasRole("sales") && !canEditAdsGuidance(auth);
  const showSalesGhlSync = showManualForm && isSalesOnly;
  const canReassignAttribution = canQuality;

  const manualIngestDirty = useMemo(
    () =>
      showManualForm &&
      isManualIngestDirty({
        body: manualForm.body,
        creativeDescription: manualForm.creativeDescription,
        externalId: manualForm.externalId,
        slotFillJsonText: manualForm.slotFillJsonText,
        platformCta: manualForm.platformCta,
        templateId: manualForm.templateId,
        qualityRating: manualForm.qualityRating,
        qualityNote: manualForm.qualityNote,
        promotedExemplar: manualForm.promotedExemplar,
        selectedCats: manualForm.selectedCats,
        selectedPostTags: manualForm.selectedPostTags,
        manualPostMediaFiles: manualForm.manualPostMediaFiles,
        publishOverrideOpen: manualForm.publishOverrideOpen,
        publishedAt: manualForm.publishedAt,
      }),
    [
      showManualForm,
      manualForm.body,
      manualForm.creativeDescription,
      manualForm.externalId,
      manualForm.slotFillJsonText,
      manualForm.platformCta,
      manualForm.templateId,
      manualForm.qualityRating,
      manualForm.qualityNote,
      manualForm.promotedExemplar,
      manualForm.selectedCats,
      manualForm.selectedPostTags,
      manualForm.manualPostMediaFiles,
      manualForm.publishOverrideOpen,
      manualForm.publishedAt,
    ],
  );

  return {
    posts,
    total,
    templates,
    categories,
    postTagDefs,
    loading,
    error,
    saveMsg,
    canIngestPosts,
    canQuality,
    canSeeSlotLabeledFields,
    canSeeGraphExternalId,
    showManualForm,
    manualIngestDirty,
    showSalesGhlSync,
    canReassignAttribution,
    load,
    orgLocations,
    orgLocationsLoaded,
    ...manualForm,
    ...indexFilters,
  };
}
